import type { i18n as I18nInstance } from 'i18next';
import useFormatDownstreamVehiclesCustom from '/business/shared/components/pages/question/formats/customDataFormats/useFormatDownstreamVehiclesCustom';
import useFormatDownstreamVehiclesSupplier from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatDownstreamVehiclesSupplier';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import educationContentJson from '/business/shared/components/questions/scope3/downstreamLeasedVehicles/_education/downstreamLeasedVehicles.json';
import useDownstreamLeasedVehiclesEstimatedSchema from '/business/shared/components/questions/scope3/downstreamLeasedVehicles/_hooks/useDownstreamLeasedVehiclesEstimatedSchema';
import DistanceCanonicalName from '/business/shared/components/widgets/misc/DistanceCanonicalName';
import VehicleTypeName from '/business/shared/components/widgets/misc/VehicleTypeName';
import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import tankerIcon from '/business/shared/images/icons/bp-road-tanker-med-pictograms-bp-green-.svg';
import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import type { QuestionConfigBranchLoaderProps, QuestionConfigQuestionType } from '/index';
import formatNumber from '/shared/functions/formatNumber';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';

export const getConfigureDownstreamLeasedVehicles = (
  _options: { i18n?: I18nInstance } = {},
): QuestionConfigQuestionType => {
  return {
    title: 'Downstream leased vehicles',
    id: 'downstream-leased-vehicles',
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.downstreamLeasedVehicles',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.downstreamLeasedVehiclesTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.downstreamLeasedVehicles',
    useHookLoader: useDownstreamLeasedVehiclesEstimatedSchema,
    apiKey: 'calculation-downstream-leased-vehicles-estimated',
    scopeCategory: questionScopeCategories.SCOPE_3,
    icon: tankerIcon,
    isCompletePropName: 'isDownstreamLeasedVehiclesComplete',
    table: {
      title: 'Calculated carbon emissions for distance based data: ',
      totalType: basketTableTotalTypes.TONNAGE,
      headings: [
        'Lessee ID',
        'Vehicle type',
        'Emission source',
        'Maximum yearly driving distance',
        'Carbon emissions',
      ].filter((item) => !!item),
      cells: [
        'lesseeId',
        ({ vehicleType }: { vehicleType: string }) => <VehicleTypeName name={vehicleType} />,
        'fuelType',
        ({ distance, distanceUnit }: { distance: number; distanceUnit: string }) => {
          return <DistanceCanonicalName distance={distance} unit={distanceUnit} type="annual" />;
        },
        ({ tonnage }: { tonnage: number }) => `${formatNumber(tonnage)} tCO\u2082e`,
      ].filter((item) => !!item),
      isAnswerEditable: true,
      tabName: 'Activity data',
      mainQuestionTitle: 'Calculated carbon emissions for downstream leased vehicles: ',
      supplier: {
        columns: [
          'lesseeId',
          'vehicleType',
          'supplierIntensityDataOrAbsoluteEmissions',
          'numberOfVehicles',
          'carbonEmissions',
        ],
      },
      custom: {
        columns: ['lesseeId', 'intensityValue', 'unitOfMeasurement', 'numberOfVehicles', 'carbonEmissions'],
      },
    },
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions for any vehicles leased to a third party business who operated the vehicles and were responsible for their energy consumption, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Lessee ID, vehicle type, type of fuel, maximum yearly driving distance.',
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData:
            'Lessee ID, vehicle make and model, vehicle type, supplier intensity data/absolute emissions, distance traveled, no. of vehicles.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData:
            'Lessee ID, vehicle make and model, vehicle type, intensity data/absolute emissions, distance traveled, no. of vehicles.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: educationContentJson,
      bottomContentsSupplier: bottomContentsSupplier,
    },
    useQuestionBranchSupplierLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatDownstreamVehiclesSupplier({
        ...props,
        questionFormatName: 'downstream-leased-vehicles',
      }),
    useQuestionBranchCustomLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatDownstreamVehiclesCustom({ ...props, questionFormatName: 'downstream-leased-vehicles' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the details for any vehicles leased to a third party business who operated the vehicles and were
          responsible for their energy consumption during the reporting period: <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the details for any vehicles leased to a third party business who operated the vehicles and were
          responsible for their energy consumption during the reporting period: <ReportingPeriod $green />?
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
